import appendQuery from 'append-query'
import { isEmpty, uniqueId } from 'lodash'
import { IMAGING_URL, jsonToFormData } from 'shared/api'
import { GET } from 'shared/util/fetch-methods'
import { SourceEditorEdits } from '../components/source-editor/types'
import CreationToolOutputType from '../types/output-type'
import getFileFromBase64 from './get-file-from-base64'
import getFramesWithEdits from './get-frames-with-edits'
import getGifFromFrames from './get-gif-from-frames'
import getSourceFrames from './get-source-frames'
import { validateSourceByOutput } from './validate-source'

const IMAGING_API_BASE_URL = `${IMAGING_URL}/imaging`

const getEditedFile = async (source: File | string, endpoint: string, props: any = {}): Promise<File> => {
    let requestUrl = `${IMAGING_API_BASE_URL}${endpoint}`
    let requestData = GET

    if (typeof source === 'string') {
        requestUrl = appendQuery(requestUrl, {
            ...props,
            media_url: encodeURIComponent(source.replace(/(^\w+:|^)\/\//, '')),
        })
    } else {
        requestData = {
            method: 'POST',
            body: jsonToFormData({
                ...props,
                file: source,
            }),
        }
    }

    const blob = await fetch(requestUrl, requestData).then(response => response.blob())

    return new File([blob], uniqueId('asset'), {
        type: blob.type,
        lastModified: Date.now(),
    })
}

const getUploadWithEdits = async (
    source: File | string,
    outputType: CreationToolOutputType,
    edits?: SourceEditorEdits
): Promise<File | string> => {
    if (!edits || isEmpty(edits)) {
        return source
    }

    const { trim, crop } = edits
    let currentFile: File | string = source

    // clips
    if (outputType === CreationToolOutputType.Video) {
        // trim
        if (trim && !isEmpty(trim)) {
            currentFile = await getEditedFile(currentFile, `/trim`, {
                start: trim.start,
                duration: trim.end - trim.start,
                fmt: 'mp4',
            })
        }

        // crop
        if (crop && !isEmpty(crop)) {
            currentFile = await getEditedFile(currentFile, `/crop`, {
                x: crop.x,
                y: crop.y,
                width: crop.width,
                height: crop.height,
                fmt: 'mp4',
            })
        }

        // watermark
        if (edits.frames?.length) {
            const watermarkUrl = edits.frames[0].canvas.toDataURL('image/png', 1)
            const watermarkFile = getFileFromBase64(watermarkUrl, 'image/png')

            currentFile = await getEditedFile(watermarkFile, `/watermark`, {
                media: currentFile,
                fmt: 'mp4',
            })
        }
    } else {
        // TODO: remove all this once we clean things up because it is trash
        const sourceFile = await validateSourceByOutput(source, outputType)
        const frames = await getSourceFrames(sourceFile)
        const editedFrames = getFramesWithEdits(frames, sourceFile.duration, edits)
        currentFile = await getGifFromFrames(editedFrames)
    }

    return currentFile
}

export default getUploadWithEdits
